/**
 * `WasdStrafeModeProvider` / `useWasdStrafeMode` (#351) — the single live
 * source of truth for the WASD strafe-axis preference. #563: a SEPARATE,
 * still-needed setting from the (now-removed) `MovementMode` — this picks
 * the axis convention WASD's four keys resolve against; it does not pick
 * a movement SCHEME (WASD is the only one now).
 *
 * State lives in React so a change from the settings screen takes effect
 * immediately (no reload), persisted via `prefs.ts`'s localStorage boundary.
 * `InputSurface` reads the effective value and stamps it onto every outgoing
 * `moveDir` message (`buildMoveDirInput`) — the sim (`@pit/sim`'s
 * `directionalDelta`) resolves the actual axis convention; this provider is
 * purely "which preference is currently selected", never a client-side
 * directional remap:
 *   - "facing" (default) → today's pre-#351 behavior, UNCHANGED: WASD strafes
 *     relative to the character's current facing.
 *   - "screen"           → WASD strafes relative to the camera's fixed
 *     screen axes, regardless of facing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_WASD_STRAFE_MODE,
  loadWasdStrafeMode as loadWasdStrafeModeFromPrefs,
  saveWasdStrafeMode as saveWasdStrafeModeToPrefs,
  type WasdStrafeMode,
} from "../prefs.js";

/** Persistence seam — injectable for tests. */
export interface WasdStrafeModePersistence {
  readonly load: () => WasdStrafeMode;
  readonly save: (value: WasdStrafeMode) => void;
}

export const PREFS_WASD_STRAFE_MODE_PERSISTENCE: WasdStrafeModePersistence = {
  load: () => loadWasdStrafeModeFromPrefs(),
  save: (value) => {
    saveWasdStrafeModeToPrefs(value);
  },
};

export interface WasdStrafeModeContextValue {
  readonly wasdStrafeMode: WasdStrafeMode;
  readonly setWasdStrafeMode: (value: WasdStrafeMode) => void;
}

const WasdStrafeModeCtx = createContext<WasdStrafeModeContextValue | null>(null);

export function WasdStrafeModeProvider({
  children,
  persistence = PREFS_WASD_STRAFE_MODE_PERSISTENCE,
}: {
  readonly children: ReactNode;
  readonly persistence?: WasdStrafeModePersistence;
}): JSX.Element {
  const [value, setValue] = useState<WasdStrafeMode>(() => persistence.load());

  const setWasdStrafeMode = useCallback(
    (next: WasdStrafeMode) => {
      persistence.save(next);
      setValue(next);
    },
    [persistence],
  );

  const ctxValue = useMemo<WasdStrafeModeContextValue>(
    () => ({ wasdStrafeMode: value, setWasdStrafeMode }),
    [value, setWasdStrafeMode],
  );

  return (
    <WasdStrafeModeCtx.Provider value={ctxValue}>{children}</WasdStrafeModeCtx.Provider>
  );
}

export function useWasdStrafeMode(): WasdStrafeModeContextValue {
  const v = useContext(WasdStrafeModeCtx);
  if (v === null) {
    throw new Error("useWasdStrafeMode: must be used within <WasdStrafeModeProvider>");
  }
  return v;
}

export { DEFAULT_WASD_STRAFE_MODE };
export type { WasdStrafeMode };
